import { isDefined } from "@clipboard-health/util-ts";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
  alpha,
  Box,
  ButtonBase,
  DialogContent,
  Divider,
  Fade,
  IconButton as MuiIconButton,
  Skeleton,
  Stack,
  Tooltip,
} from "@mui/material";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { Button } from "@src/appV2/redesign/components/Button";
import { DialogFooter } from "@src/appV2/redesign/components/DialogFooter";
import { TextButton } from "@src/appV2/redesign/components/TextButton";
import { type TimeSlot } from "@src/appV2/redesign/Shift/Open/types";
import { TimeSlotPill } from "@src/appV2/redesign/Shift/TimeSlotPill";
import { Text, Title } from "@src/appV2/redesign/system/ui-components";
import { useGetWorkplaceProfile } from "@src/appV2/redesign/Workplace/useGetWorkplaceProfile";
import { formatDistanceToNow } from "date-fns";
import { useState } from "react";

import { type ComparisonPairWorkplace, type ComparisonSelection, type StepAction } from "../types";

interface CompareWorkplacesStepProps {
  workplaceA: ComparisonPairWorkplace;
  workplaceB: ComparisonPairWorkplace;
  shiftId: string | null;
  onAction: (action: StepAction) => void;
}

interface WorkplaceCardProps {
  workplaceName: string;
  address?: string;
  isAddressLoading?: boolean;
  timeSlot?: TimeSlot;
  lastWorkedAt?: string;
  isSelected: boolean;
  isDimmed: boolean;
  onSelect: () => void;
}

function WorkplaceCard(props: WorkplaceCardProps) {
  const {
    workplaceName,
    address,
    isAddressLoading,
    timeSlot,
    lastWorkedAt,
    isSelected,
    isDimmed,
    onSelect,
  } = props;

  const relativeDate = lastWorkedAt
    ? formatDistanceToNow(new Date(lastWorkedAt), { addSuffix: true })
    : undefined;

  return (
    <ButtonBase
      sx={(theme) => ({
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        gap: 2,
        p: 4,
        borderRadius: 3,
        border: `2px solid ${isSelected ? theme.palette.primary.main : theme.palette.divider}`,
        backgroundColor: isSelected
          ? alpha(theme.palette.primary.main, 0.04)
          : theme.palette.background.paper,
        opacity: isDimmed ? 0.5 : 1,
        transition: "border-color 0.2s, background-color 0.2s, opacity 0.2s",
        textAlign: "left",
        width: "100%",
      })}
      onClick={onSelect}
    >
      <Text semibold variant="body1">
        {workplaceName}
      </Text>
      {isAddressLoading && <Skeleton variant="text" width="70%" height={20} />}
      {isDefined(address) && (
        <Text variant="body2" color="text.secondary">
          {address}
        </Text>
      )}
      {(isDefined(timeSlot) || isDefined(relativeDate)) && (
        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          divider={
            <Text variant="caption" color="text.secondary">
              ·
            </Text>
          }
        >
          {isDefined(timeSlot) && <TimeSlotPill timeSlot={timeSlot} />}
          {isDefined(relativeDate) && (
            <Text variant="caption" color="text.secondary">
              {relativeDate}
            </Text>
          )}
        </Stack>
      )}
    </ButtonBase>
  );
}

export function CompareWorkplacesStep(props: CompareWorkplacesStepProps) {
  const { workplaceA, workplaceB, shiftId, onAction } = props;
  const [selection, setSelection] = useState<ComparisonSelection | undefined>(undefined);

  const { data: workplaceBProfile, isLoading: isWorkplaceBProfileLoading } = useGetWorkplaceProfile(
    {
      workplaceId: workplaceB.workplaceId,
    }
  );

  const workplaceBAddress = workplaceBProfile?.fullAddress?.formatted;

  function logComparisonInteraction(submittedSelection: "workplaceA" | "workplaceB" | "tie") {
    const actionMap = {
      tie: "tie",
      workplaceA: "select_a",
      workplaceB: "select_b",
    } as const;
    logEvent(APP_V2_USER_EVENTS.WORKPLACE_COMPARISON_INTERACTION, {
      action: actionMap[submittedSelection],
      shiftId,
      workplaceAId: workplaceA.workplaceId,
      workplaceBId: workplaceB.workplaceId,
    });
  }

  function handleContinue() {
    if (!isDefined(selection)) {
      return;
    }

    logComparisonInteraction(selection);
    onAction({ type: "NEXT_STEP", data: { comparisonSelection: selection } });
  }

  function handleTie() {
    logComparisonInteraction("tie");
    onAction({ type: "NEXT_STEP", data: { comparisonSelection: "tie" } });
  }

  return (
    <>
      <DialogContent sx={{ display: "flex", flexDirection: "column", flex: 1 }}>
        <Stack spacing={2} sx={{ mb: 5 }}>
          <Text
            variant="caption"
            sx={{
              textTransform: "uppercase",
              letterSpacing: 1,
              color: "text.secondary",
            }}
          >
            YOUR RECENT SHIFTS
          </Text>
          <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
            <Box sx={{ flex: 1 }}>
              <Title variant="h4" component="h2">
                Where would you rather work again?
              </Title>
            </Box>
            <Tooltip
              arrow
              enterTouchDelay={50}
              leaveTouchDelay={10000}
              title={
                <Stack spacing={1} sx={{ p: 1 }}>
                  <Text variant="body2" sx={{ fontWeight: 600, color: "inherit" }}>
                    Why are we asking?
                  </Text>
                  <Text variant="caption" sx={{ color: "inherit" }}>
                    Your pick helps us calculate the star rating other professionals see when
                    browsing workplaces.
                  </Text>
                </Stack>
              }
            >
              <MuiIconButton size="small" sx={{ mt: 1 }}>
                <InfoOutlinedIcon fontSize="small" />
              </MuiIconButton>
            </Tooltip>
          </Stack>
          <Text variant="body2" color="text.secondary">
            Tap the one you'd choose for your next shift.
          </Text>
        </Stack>

        <Stack spacing={5} sx={{ flex: 1 }}>
          <WorkplaceCard
            workplaceName={workplaceA.workplaceName}
            address={workplaceA.address}
            timeSlot={workplaceA.timeSlot}
            lastWorkedAt={workplaceA.lastWorkedAt}
            isSelected={selection === "workplaceA"}
            isDimmed={isDefined(selection) && selection !== "workplaceA" && selection !== "tie"}
            onSelect={() => {
              setSelection("workplaceA");
            }}
          />

          <Divider sx={{ py: 1 }}>
            <Text variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
              VS
            </Text>
          </Divider>

          <WorkplaceCard
            workplaceName={workplaceB.workplaceName}
            address={workplaceBAddress}
            isAddressLoading={isWorkplaceBProfileLoading}
            timeSlot={workplaceB.timeSlot}
            lastWorkedAt={workplaceB.lastWorkedAt}
            isSelected={selection === "workplaceB"}
            isDimmed={isDefined(selection) && selection !== "workplaceB" && selection !== "tie"}
            onSelect={() => {
              setSelection("workplaceB");
            }}
          />
        </Stack>
      </DialogContent>

      <DialogFooter orientation="vertical">
        <Stack spacing={3} sx={{ width: "100%" }}>
          <Fade in={isDefined(selection)}>
            <Box>
              <Button
                fullWidth
                size="large"
                variant="contained"
                disabled={!isDefined(selection)}
                onClick={() => handleContinue()}
              >
                Continue
              </Button>
            </Box>
          </Fade>
          <TextButton sx={{ alignSelf: "center" }} onClick={() => handleTie()}>
            It's a tie
          </TextButton>
        </Stack>
      </DialogFooter>
    </>
  );
}
